import React, {useState, useEffect, useRef, useCallback, useMemo} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {
    ChevronLeft, ChevronRight, Plus, Trash2, User, Clock,
    CheckCircle, AlertCircle, XCircle, AlertTriangle,
    ChevronDown, List, Calendar as CalendarIcon,
    RefreshCw, CalendarDays, CalendarRange, Pencil, CheckCircle2, CircleCheckBig,
    RotateCcw, MessageCircle, Copy, CalendarX2, CalendarOff
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { DateCalendar } from '../components/Common/DateInput';
import secureStorage from "../utils/secureStorage";
import { apiDateToWallClock, wallClockToApi } from "../utils/dateHelpers";
import {api} from "../config/api";
import {useSyncQueue} from "./useSyncQueue";
import {fetchAllEvents} from './eventsApi';
import DeletedEventsModal from './DeletedEventsModal';
import {STATUS_OPTIONS, statusStyle} from './calendarColors';
import AppointmentCard from './AppointmentCard';
import {WaitingListModal, WaitingListView, EventModal, DeleteConfirmModal, CancelEventModal, RestDayModal} from './CalendarModals';
import {
    CALENDAR_HOURS_KEY, REST_DAYS_KEY, REST_HATCH, findRestConflict, getDayHours, isDayClosed, isFullDayRest,
    isLeave, restDaysForDay, restHoursLabel, restKindLabel, saveRestSlots
} from './restDays';
import {
    apiErrorMessage, createRestDays, deleteRestDay, fetchCalendarHours, fetchRestDays, updateRestDay
} from '../components/Setting/Calendar/calendarSettingsApi';
import usePermissions from '../hooks/usePermissions';
import {sendEventWhatsApp, WHATSAPP_FEEDBACK} from './eventWhatsApp';
import {PageHeader, HeaderAction, SearchInput, ToolbarSelect, ActiveFilters, ListEmpty, ListLoader} from '../stock/StockUI';
import WeekView from './WeekView';
import MonthView from './MonthView';
import {CALENDAR_VIEWS, addDays, dayKey, getViewRange, getWeekDays, isCurrentPeriod, shiftDate} from './calendarRange';
import Button from '../components/Common/Button';
import Notification from "../components/Common/Notification";


const RESOURCE_COLORS = ['#4DB6AC','#FF7043','#42A5F5','#AB47BC','#66BB6A','#FFA726','#5C6BC0','#FFCA28'];

// ─── Timeline layout constants ─────────────────────────────────────────────────
const TIMELINE_HOURS = Array.from({length: 14}, (_, i) => i + 9); // 9 → 22
const TIME_SLOT_WIDTH = 130; // px per hour column
const TOTAL_TIMELINE_WIDTH = TIMELINE_HOURS.length * TIME_SLOT_WIDTH;
const TIMELINE_START = TIMELINE_HOURS[0];
const TIMELINE_END = TIMELINE_HOURS[TIMELINE_HOURS.length - 1] + 1;
const ROW_HEIGHT = 120; // px per resource row

const VIEW_TABS = [
    {key: 'day', label: 'Jour', Icon: CalendarIcon},
    {key: 'week', label: 'Semaine', Icon: CalendarRange},
    {key: 'month', label: 'Mois', Icon: CalendarDays},
];
const VIEW_STORAGE_KEY = 'calendarView';
const PERIOD_LABELS = {day: 'Jour', week: 'Semaine', month: 'Mois'};

// Last view used on this browser (per-viewer convenience, falls back to "Jour")
const readStoredView = () => {
    try {
        const v = localStorage.getItem(VIEW_STORAGE_KEY);
        return CALENDAR_VIEWS.includes(v) ? v : 'day';
    } catch {
        return 'day';
    }
};

const Calendar = () => {
    // State management
    const [currentDate, setCurrentDate] = useState(new Date());
    const [filterDate, setFilterDate] = useState(null);
    const [currentView, setCurrentView] = useState(readStoredView);
    const eventsRequestRef = useRef(0); // drops responses of superseded loads
    const [events, setEvents] = useState([]);
    const [resources, setResources] = useState([]);
    const [clients, setClients] = useState([]);
    const [services, setServices] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [selectedResource, setSelectedResource] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState('name-asc');
    const [searchTerm, setSearchTerm] = useState('');
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [waitingList, setWaitingList] = useState([]);
    const [showWaitingListModal, setShowWaitingListModal] = useState(false);
    const [showWaitingListView, setShowWaitingListView] = useState(false);
    const [selectedWaitingItem, setSelectedWaitingItem] = useState(null);
    const [notification, setNotification] = useState({show: false, type: '', message: ''});
    const [showDeletedEvents, setShowDeletedEvents] = useState(false);
    const [cancelTarget, setCancelTarget] = useState(null);       // RDV being cancelled (reason modal)
    const [duplicateSource, setDuplicateSource] = useState(null); // RDV copied into a new one
    const [restModal, setRestModal] = useState(null);             // {initial, id?} — Repos / Congé form
    const [restSaving, setRestSaving] = useState(false);

    // ─── Employees' rest days / leave + opening hours (Réglages > Calendrier) ─
    // Same rows the tablets enforce; shared query keys, so an edit here or in
    // Réglages refreshes both screens.
    const queryClient = useQueryClient();
    const {canUpdate} = usePermissions();
    const canManageRest = canUpdate('calendar') || canUpdate('settings');
    const restFrom = dayKey(getViewRange(currentDate, currentView)[0]);
    const {data: restData} = useQuery({
        queryKey: [...REST_DAYS_KEY, restFrom],
        queryFn: () => fetchRestDays({from: restFrom}),
        staleTime: 60 * 1000,
    });
    const restDays = useMemo(() => restData?.restDays || [], [restData]);
    const {data: openingHours = null} = useQuery({
        queryKey: CALENDAR_HOURS_KEY,
        queryFn: fetchCalendarHours,
        staleTime: 5 * 60 * 1000,
    });
    // serviceSearch / serviceSort moved into EventModal local state

    // ─── Sync-queue (flushFn wired after API helpers are defined below) ───────
    const syncFlushRef = useRef(null);
    const { enqueue, retryFailed, isSyncing, pendingCount, failedCount } =
        useSyncQueue(useCallback(async (item) => {
            if (syncFlushRef.current) return syncFlushRef.current(item);
            throw new Error('sync not ready');
        // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []));

    // ─── Resource filter & scroll-sync refs ───────────────────────────────────
    const [selectedResourceIds, setSelectedResourceIds] = useState(null); // null = all visible
    const headerScrollRef = useRef(null);
    const rowScrollRefs   = useRef([]);

    // ─── Scroll synchronisation ───────────────────────────────────────────────
    const syncHScroll = useCallback((e) => {
        const xPos = e.target.scrollLeft;
        requestAnimationFrame(() => {
            if (headerScrollRef.current && headerScrollRef.current !== e.target)
                headerScrollRef.current.scrollLeft = xPos;
            rowScrollRefs.current.forEach(ref => {
                if (ref && ref !== e.target) ref.scrollLeft = xPos;
            });
        });
    }, []);

    // ─── Resource filter helpers ───────────────────────────────────────────────
    const toggleResource = useCallback((id) => {
        setSelectedResourceIds(prev => {
            if (prev === null) return new Set([id]);
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); return next.size === 0 ? null : next; }
            else { next.add(id); return next; }
        });
    }, []);

    const selectOnlyResource = useCallback((id) => {
        setSelectedResourceIds(prev =>
            prev !== null && prev.size === 1 && prev.has(id) ? null : new Set([id])
        );
    }, []);

    const selectAllResources = useCallback(() => setSelectedResourceIds(null), []);

    // ─── Event-positioning helpers ────────────────────────────────────────────
    const getEventLeft = useCallback((event) => {
        const h = event.start.getHours();
        const m = event.start.getMinutes();
        return Math.max((h - 9) * TIME_SLOT_WIDTH + (m / 60) * TIME_SLOT_WIDTH, 0);
    }, []);

    const getEventWidth = useCallback((event) => {
        const durationHours = (event.end - event.start) / (1000 * 60 * 60);
        return Math.max(durationHours * TIME_SLOT_WIDTH - 4, 52);
    }, []);

    /**
     * For a list of events in the same resource row, compute for each event:
     *   lane       – vertical slot index (0-based) within overlapping groups
     *   totalLanes – number of concurrent lanes in that event's overlap group
     * Returns a map: eventId → { lane, totalLanes }
     */
    const computeEventLanes = useCallback((eventsInRow) => {
        // Sort by start time so greedy lane assignment works correctly
        const sorted = [...eventsInRow].sort((a, b) => a.start - b.start);
        const lanes = {};          // eventId → lane index
        const laneEndTimes = [];   // earliest free time per lane

        sorted.forEach(event => {
            let assignedLane = laneEndTimes.findIndex(t => t <= event.start);
            if (assignedLane === -1) {
                assignedLane = laneEndTimes.length;
                laneEndTimes.push(event.end);
            } else {
                laneEndTimes[assignedLane] = event.end;
            }
            lanes[event.id] = assignedLane;
        });

        // totalLanes for each event = highest lane index among its concurrent peers + 1
        const result = {};
        sorted.forEach(event => {
            const concurrent = sorted.filter(
                other => other.start < event.end && other.end > event.start
            );
            const maxLane = Math.max(...concurrent.map(e => lanes[e.id]));
            result[event.id] = { lane: lanes[event.id], totalLanes: maxLane + 1 };
        });
        return result;
    }, []);

    // ─── Computed: event counts per resource ──────────────────────────────────
    // (defined after state; events may not be loaded yet — safe to use useMemo)
    // NOTE: these are placed up here so they are in scope for the filter bar render.
    // Because they access `events` and `resources` (state), they are recalculated
    // on every render where those change.

    // getFilteredServices moved into EventModal local scope

    // Helper functions
    const showNotification = (type, message) => {
        setNotification({show: true, type, message});
        setTimeout(() => setNotification({show: false, type: '', message: ''}), 5000);
    };

    const getRandomColor = () => {
        return RESOURCE_COLORS[Math.floor(Math.random() * RESOURCE_COLORS.length)];
    };

    // API functions
    // API functions
    const fetchClients = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/clients', {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });
            // response.data may be an array directly, or wrapped in .data
            const raw = response.data;
            if (Array.isArray(raw)) return raw;
            if (Array.isArray(raw?.data)) return raw.data;
            if (Array.isArray(raw?.items)) return raw.items;
            return [];
        } catch (error) {
            console.error('Failed to fetch clients:', error);
            return [];
        }
    };

    const fetchEmployees = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get('/employees?page=1', {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });
            const raw = response.data;
            if (Array.isArray(raw)) return raw;
            if (Array.isArray(raw?.items)) return raw.items;
            if (Array.isArray(raw?.data)) return raw.data;
            return [];
        } catch (error) {
            console.error('Failed to fetch employees:', error);
            return [];
        }
    };

    const fetchServices = async (searchTerm = '') => {
        try {
            console.log('Fetching services with search term:', searchTerm);
            const accountId = await secureStorage.getItem("selectedAccount");

            // Build the URL with search parameter if provided
            let url = '/products';
            if (searchTerm) {
                url += `?name=${encodeURIComponent(searchTerm)}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            console.log('Services response:', response.data);
            const raw = response.data;
            if (Array.isArray(raw)) return raw;
            if (Array.isArray(raw?.data)) return raw.data;
            if (Array.isArray(raw?.items)) return raw.items;
            return [];
        } catch (error) {
            console.error('Failed to fetch services:', error);
            return [];
        }
    };

    const searchServicesAPI = async (term, setLoadingFn = null) => {
        if (!term || term.length < 2) return;
        try {
            if (setLoadingFn) setLoadingFn(true);
            const results = await fetchServices(term);
            const safeResults = Array.isArray(results) ? results : [];
            // Merge results with existing services, avoiding duplicates (functional update avoids stale closure)
            setServices(prev => {
                const existingIds = prev.map(s => s.id);
                const newItems = safeResults.filter(s => !existingIds.includes(s.id));
                return newItems.length > 0 ? [...prev, ...newItems] : prev;
            });
            if (setLoadingFn) setLoadingFn(false);
        } catch (error) {
            console.error('Error searching services:', error);
            if (setLoadingFn) setLoadingFn(false);
        }
    };

    const fetchEvents = async (startDate, endDate) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Use Unix timestamps (seconds) for API params since the API stores dates as Unix timestamps
            // Padded by 3h: RDVs are stored as UTC instants of the salon's wall
            // clock, so one just after midnight can sit before the browser's day.
            const RANGE_PAD_S = 3 * 3600;
            const dateFromUnix = Math.floor(startDate.getTime() / 1000) - RANGE_PAD_S;
            const dateToUnix = Math.floor(endDate.getTime() / 1000) + RANGE_PAD_S;

            const rawEvents = await fetchAllEvents(accountId.id, {
                date_from: dateFromUnix,
                date_to: dateToUnix
            });

            if (!rawEvents || rawEvents.length === 0) {
                console.log('No events returned from API');
                return [];
            }

            const processedEvents = rawEvents
                .filter(event => event && event.employee) // client is optional - some events don't have one
                .map(event => {
                    // Get employee ID and name from the employee object
                    const employeeId = event.employee?.id;
                    const employeeName = event.employee?.name || '';

                    // Get client ID and name from the client object (may be absent)
                    const clientId = event.client?.id || null;
                    const clientName = event.client
                        ? `${event.client.firstName || ''} ${event.client.lastName || ''}`.trim()
                        : (event.title || '');

                    // Parse services if available
                    const services = event.service ? event.service.split(',').map(s => s.trim()).filter(Boolean) : [];

                    // Create proper Date objects - API returns Unix timestamps (seconds)
                    // Detect if it's a Unix timestamp (number) or ISO string
                    // Salon wall clock, same as the tablets (see apiDateToWallClock)
                    const eventStart = apiDateToWallClock(event.start);
                    const eventEnd = apiDateToWallClock(event.end);

                    // Validate dates
                    if (isNaN(eventStart.getTime()) || isNaN(eventEnd.getTime())) {
                        console.error('Invalid event dates for event:', event.id, event.start, event.end);
                        return null;
                    }

                    return {
                        id: event.id,
                        title: event.title || 'Rendez-vous',
                        start: eventStart,
                        end: eventEnd,
                        resourceId: employeeId,
                        resourceName: employeeName,
                        clientId: clientId,
                        clientName: clientName,
                        clientInfo: {
                            firstName: event.client?.firstName || '',
                            lastName: event.client?.lastName || '',
                            phone: event.client?.phone || '',
                            countryCode: event.client?.countryCode || event.client?.country_code || '',
                            email: event.client?.email || '',
                            birthday: event.client?.birthday || '',
                            address: event.client?.adresse || '',
                            city: event.client?.city || '',
                            active: event.client?.active ?? true
                        },
                        employeeInfo: {
                            name: event.employee?.name || '',
                            email: event.employee?.email || ''
                        },
                        status: event.status || 'confirmed',
                        selectedServices: services,
                        bgColor: event.color || getEventColor(event.status || 'confirmed'),
                        color: event.color || '#3B82F6',
                        // The tablets write the RDV note in `remarque`
                        notes: event.remarque || event.notes || '',
                        duration: event.duration,
                        externalId: event.externalId || event.external_id
                    };
                })
                .filter(Boolean); // Remove null entries from invalid dates

            console.log('Processed events:', processedEvents.length, processedEvents);
            return processedEvents;
        } catch (error) {
            console.error('Failed to fetch events:', error);
            showNotification('error', 'Erreur lors du chargement des rendez-vous');
            return [];
        }
    };


    const createEvent = async (eventData) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Validate required fields
            if (!eventData.resourceId) {
                throw new Error('Employee is required');
            }
            if (!eventData.clientId) {
                throw new Error('Client is required');
            }

            // Préparer les données selon le format de l'API événement
            const formData = new FormData();

            // Générer un ID unique pour le nouvel événement
            formData.append('id', crypto.randomUUID());
            formData.append('title', eventData.title || 'Rendez-vous');

            // Calculer la durée en heures
            const durationHours = (new Date(eventData.end) - new Date(eventData.start)) / (1000 * 60 * 60);
            formData.append('duration', durationHours.toString());

            formData.append('color', eventData.color || '#3B82F6'); // couleur par défaut
            // Wall clock without offset — stored by the API exactly like the tablets' RDVs
            formData.append('start', wallClockToApi(eventData.start));
            formData.append('end', wallClockToApi(eventData.end));

            // Ensure employee_id is properly set
            formData.append('employee_id', eventData.resourceId);
            formData.append('client_id', eventData.clientId);

            // Convertir les services sélectionnés en chaîne
            // Prefer _serviceNames injected by EventModal (from ReferencePickerModal objects)
            const serviceStr = eventData._serviceNames?.join(',') ||
                (eventData.selectedServices || [])
                    .map(sid => services.find(s => s.id === sid)?.name || '')
                    .filter(Boolean)
                    .join(',');
            formData.append('service', serviceStr || '');

            formData.append('external_id', Date.now().toString()); // ID externe unique
            formData.append('status', eventData.status || 'confirmed');
            formData.append('remarque', eventData.notes || '');

            console.log('Creating event with data:', {
                id: crypto.randomUUID(),
                title: eventData.title,
                employee_id: eventData.resourceId,
                client_id: eventData.clientId,
                start: Math.floor(new Date(eventData.start).getTime() / 1000),
                end: Math.floor(new Date(eventData.end).getTime() / 1000),
                duration: durationHours
            });

            const response = await api.post('/evenement', formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Transformer la réponse pour correspondre au format attendu
            const newEvent = {
                id: response.data.id,
                title: response.data.title,
                start: apiDateToWallClock(response.data.start),
                end: apiDateToWallClock(response.data.end),
                resourceId: eventData.resourceId,
                clientId: eventData.clientId,
                clientName: eventData.clientName,
                status: response.data.status || 'confirmed',
                notes: eventData.notes || '',
                selectedServices: eventData._serviceNames || eventData.selectedServices || [],
                clientInfo: eventData.clientInfo,
                color: response.data.color,
                service: response.data.service
            };

            return newEvent;
        } catch (error) {
            console.error('Failed to create event:', error);
            console.error('Error response:', error.response?.data);
            throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Erreur lors de la création de l\'événement');
        }
    };


    const updateEvent = async (id, eventData) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Validate required fields
            if (!eventData.resourceId) {
                throw new Error('Employee is required');
            }

            // Préparer les données selon le format de l'API événement
            const formData = new FormData();

            formData.append('id', eventData.id || id);
            formData.append('title', eventData.title || 'Rendez-vous');

            // Calculer la durée en heures
            const durationHours = (new Date(eventData.end) - new Date(eventData.start)) / (1000 * 60 * 60);
            formData.append('duration', durationHours.toString());

            formData.append('color', eventData.color || '#3B82F6');
            // Wall clock without offset — stored by the API exactly like the tablets' RDVs
            formData.append('start', wallClockToApi(eventData.start));
            formData.append('end', wallClockToApi(eventData.end));

            // Ensure employee_id is properly set
            formData.append('employee_id', eventData.resourceId);
            if (eventData.clientId) formData.append('client_id', eventData.clientId);

            // Convertir les services sélectionnés en chaîne
            // Prefer _serviceNames injected by EventModal (from ReferencePickerModal objects)
            const serviceStr = eventData._serviceNames?.join(',') ||
                (eventData.selectedServices || [])
                    .map(sid => services.find(s => s.id === sid)?.name || '')
                    .filter(Boolean)
                    .join(',');
            formData.append('service', serviceStr || '');

            formData.append('external_id', eventData.external_id || eventData.externalId || Date.now().toString());
            formData.append('status', eventData.status || 'confirmed');
            formData.append('remarque', eventData.notes || '');

            console.log('Updating event with data:', {
                id: eventData.id || id,
                title: eventData.title,
                employee_id: eventData.resourceId,
                client_id: eventData.clientId,
                start: Math.floor(new Date(eventData.start).getTime() / 1000),
                end: Math.floor(new Date(eventData.end).getTime() / 1000),
                duration: durationHours
            });

            const response = await api.put(`/evenement/${id}`, formData, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json',
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Transformer la réponse pour correspondre au format attendu
            const updatedEvent = {
                id: response.data.id,
                title: response.data.title,
                start: apiDateToWallClock(response.data.start),
                end: apiDateToWallClock(response.data.end),
                resourceId: eventData.resourceId,
                clientId: eventData.clientId,
                clientName: eventData.clientName,
                status: response.data.status || 'confirmed',
                notes: eventData.notes || '',
                selectedServices: eventData._serviceNames || eventData.selectedServices || [],
                clientInfo: eventData.clientInfo,
                externalId: eventData.externalId || eventData.external_id,
                color: response.data.color,
                service: response.data.service
            };

            return updatedEvent;
        } catch (error) {
            console.error('Failed to update event:', error);
            console.error('Error response:', error.response?.data);
            throw new Error(error.response?.data?.detail || error.response?.data?.message || 'Erreur lors de la mise à jour de l\'événement');
        }
    };

    const deleteEventById = async (id) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            await api.delete(`/evenement/${id}`, {
                headers: { 'X-account-id': accountId.id, 'Accept': 'application/json' }
            });
            return true;
        } catch (error) {
            console.error('Failed to delete event:', error);
            throw new Error(error.response?.data?.message || 'Erreur lors de la suppression de l\'événement');
        }
    };

    // ─── Wire syncFlushRef once API helpers are in scope ─────────────────────
    useEffect(() => {
        syncFlushRef.current = async (item) => {
            if (item.operation === 'create') {
                const serverEvent = await createEvent(item.payload);
                setEvents(prev => prev.map(e =>
                    e.id === item.payload._localId ? { ...serverEvent, _syncStatus: 'synced' } : e
                ));
            } else if (item.operation === 'update') {
                const serverEvent = await updateEvent(item.payload.id, item.payload);
                setEvents(prev => prev.map(e =>
                    e.id === item.payload.id ? { ...serverEvent, _syncStatus: 'synced' } : e
                ));
            } else if (item.operation === 'delete') {
                await deleteEventById(item.payload.id);
                setEvents(prev => prev.filter(e => e.id !== item.payload.id));
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getEventDetails = async (id) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const response = await api.get(`/evenement/${id}`, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            const data = response.data;

            // Handle Unix timestamp or ISO string for dates
            const startDate = apiDateToWallClock(data.start);
            const endDate = apiDateToWallClock(data.end);

            return {
                ...data,
                start: startDate,
                end: endDate
            };
        } catch (error) {
            console.error('Failed to fetch event details:', error);
            throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des détails de l\'événement');
        }
    };

    // Data initialization
    useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);

                const [clientsData, employeesData, servicesData] = await Promise.all([
                    fetchClients(),
                    fetchEmployees(),
                    fetchServices()
                ]);

                setClients((clientsData || []).map(client => ({
                    ...client,
                    firstName: client.firstName || '',
                    lastName: client.lastName || '',
                    phone: client.phone || '',
                    email: client.email || '',
                    fullName: `${client.firstName || ''} ${client.lastName || ''}`,
                    contact: `${client.phone || ''} • ${client.email || ''}`
                })));

                setResources((employeesData || [])
                    .filter(emp => emp.showOnCalendar !== false)
                    .map((emp, index) => ({
                    id: emp.id,
                    name: `${emp.name}`,
                    specialty: emp.profil?.label || 'General',
                    color: RESOURCE_COLORS[index % RESOURCE_COLORS.length]
                })));

                setServices((servicesData || []).map(svc => ({
                    id: svc.id,
                    name: svc.name,
                    duration: svc.duration || 30,
                    price: svc.price || 0
                })));

                // Load events for current date range
                await loadEventsForCurrentView();

                setIsLoading(false);
            } catch (error) {
                console.error('Failed to load data:', error);
                showNotification('error', 'Failed to load calendar data');
                setIsLoading(false);
            }
        };

        loadData();
    }, []);


    // Function to load events based on current view
    const loadEventsForCurrentView = async (dateOverride, viewOverride) => {
        const requestId = ++eventsRequestRef.current;
        try {
            // Use the supplied date/view, or fall back to the current state values
            const targetDate = dateOverride instanceof Date ? dateOverride : currentDate;
            const [startDate, endDate] = getViewRange(targetDate, viewOverride || currentView);

            console.log('Fetching events from', startDate, 'to', endDate);
            const eventsData = await fetchEvents(startDate, endDate);
            if (requestId !== eventsRequestRef.current) return;
            setEvents(eventsData);
        } catch (error) {
            console.error('Failed to load events:', error);
            showNotification('error', 'Failed to load events');
        }
    };


    // Reload events whenever the selected date changes
    useEffect(() => {
        if (resources.length > 0) {
            // Pass currentDate explicitly to avoid stale-closure issues
            loadEventsForCurrentView(currentDate, currentView);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentDate, currentView, resources.length]);


    // Duplicate useEffect removed — initial data load is handled by the useEffect above

    // Event handlers
    // Same rule as the tablets: no RDV during an employee's Repos / Congé
    const rejectRestConflict = (eventData) => {
        const rest = findRestConflict(restDays, eventData.resourceId, new Date(eventData.start), new Date(eventData.end));
        if (rest) showNotification('error', `Ce créneau tombe pendant un ${restKindLabel(rest).toLowerCase()} de cet employé.`);
        return Boolean(rest);
    };

    const handleAddEvent = async (eventData) => {
        if (!eventData.resourceId) { showNotification('error', 'Veuillez sélectionner un employé'); return; }
        if (!eventData.clientId)   { showNotification('error', 'Veuillez sélectionner un client');  return; }
        if (rejectRestConflict(eventData)) return;

        const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const localEvent = {
            id: localId, title: eventData.title || 'Rendez-vous',
            start: new Date(eventData.start), end: new Date(eventData.end),
            resourceId: eventData.resourceId, clientId: eventData.clientId,
            clientName: eventData.clientName, status: eventData.status || 'pending',
            notes: eventData.notes || '', selectedServices: eventData.selectedServices || [],
            color: eventData.color || '#4DB6AC', _syncStatus: 'pending',
        };
        setEvents(prev => [...prev, localEvent]);
        setShowAddModal(false); setSelectedTimeSlot(null); setSelectedResource(null);

        const payload = { ...eventData, _localId: localId };
        try {
            const serverEvent = await createEvent(payload);
            setEvents(prev => prev.map(e => e.id === localId ? { ...serverEvent, _syncStatus: 'synced' } : e));
            showNotification('success', 'Rendez-vous créé et synchronisé ✓');
        } catch {
            enqueue('create', payload);
            showNotification('warning', 'Hors ligne – RDV enregistré localement, synchronisation en attente…');
        }
    };

    const handleUpdateEvent = async (eventData) => {
        if (!eventData.resourceId) { showNotification('error', 'Veuillez sélectionner un employé'); return; }
        if (!eventData.clientId)   { showNotification('error', 'Veuillez sélectionner un client');  return; }
        if (rejectRestConflict(eventData)) return;

        // Keep what the form doesn't carry (client phone for WhatsApp, external id)
        const current = events.find(e => e.id === eventData.id) || {};
        const payload = {
            ...eventData,
            externalId: current.externalId,
            clientInfo: current.clientId === eventData.clientId ? current.clientInfo : undefined,
        };
        const optimistic = {
            ...current, ...payload,
            start: new Date(eventData.start), end: new Date(eventData.end),
            selectedServices: eventData._serviceNames || current.selectedServices || [],
            _syncStatus: 'pending',
        };
        setEvents(prev => prev.map(e => e.id === eventData.id ? optimistic : e));
        setShowEditModal(false); setSelectedEvent(null);

        try {
            const serverEvent = await updateEvent(eventData.id, payload);
            setEvents(prev => prev.map(e => e.id === eventData.id ? { ...e, ...serverEvent, _syncStatus: 'synced' } : e));
            showNotification('success', 'Rendez-vous modifié et synchronisé ✓');
        } catch {
            enqueue('update', payload);
            showNotification('warning', 'Hors ligne – modification enregistrée localement, synchronisation en attente…');
        }
    };

    // ─── Per-appointment actions (same set as the POS tablets) ────────────────
    // Status changes go through the regular PUT /evenement/{id} with the RDV's
    // current data, optimistic like the form, queued when offline.
    const changeEventStatus = async (event, status, changes = {}, successMessage) => {
        const payload = {...event, ...changes, status, _serviceNames: event.selectedServices};
        setEvents(prev => prev.map(e => e.id === event.id ? {...e, ...changes, status, _syncStatus: 'pending'} : e));
        closeEventModals();
        try {
            const serverEvent = await updateEvent(event.id, payload);
            setEvents(prev => prev.map(e => e.id === event.id ? {...e, ...serverEvent, _syncStatus: 'synced'} : e));
            showNotification('success', successMessage);
        } catch {
            enqueue('update', payload);
            showNotification('warning', 'Hors ligne – changement enregistré localement, synchronisation en attente…');
        }
    };

    const notifyClientWhatsApp = async (event, status) => {
        const account = await secureStorage.getItem('selectedAccount');
        const result = await sendEventWhatsApp({
            accountId: account?.id,
            event,
            status,
            professional: resources.find(r => r.id === event.resourceId)?.name || '',
            shopName: account?.name || '',
            shopPhone: account?.phone || '',
        });
        const [type, message] = WHATSAPP_FEEDBACK[result.status] || WHATSAPP_FEEDBACK.failed;
        showNotification(type, message);
    };

    const handleCancelAppointment = async (event, {reason, notify}) => {
        setCancelTarget(null);
        const notes = reason ? `ANNULÉ: ${reason}\n${event.notes || ''}`.trim() : event.notes;
        await changeEventStatus(event, 'cancelled', {notes}, 'Rendez-vous annulé');
        if (notify) notifyClientWhatsApp({...event, notes, status: 'cancelled'}, 'cancelled');
    };

    // ─── Repos / Congé from the calendar ──────────────────────────────────────
    const openNewRest = (employeeId = null) => {
        const day = filterDate || currentDate;
        const ids = employeeId ? [employeeId]
            : selectedResourceIds?.size === 1 ? [...selectedResourceIds] : [];
        setRestModal({initial: {
            employee_ids: ids,
            slots: [{days_of_week: [day.getDay()], start_time: 9, end_time: 23}],
            start_date: dayKey(day),
            end_date: dayKey(addDays(day, 30)),
            recurring: true,
        }});
    };

    const openEditRest = (rest) => {
        if (!canManageRest) return;
        setRestModal({id: rest.id, initial: {
            employee_ids: rest.employee_id ? [rest.employee_id] : [],
            slots: [{days_of_week: rest.days_of_week, start_time: rest.start_time, end_time: rest.end_time}],
            start_date: rest.start_date,
            end_date: rest.end_date,
            recurring: rest.recurring,
        }});
    };

    const saveRest = async (form) => {
        setRestSaving(true);
        const {saved, total, error} = await saveRestSlots(form, {
            id: restModal?.id, create: createRestDays, update: updateRestDay,
        });
        setRestSaving(false);
        if (saved > 0) queryClient.invalidateQueries({queryKey: REST_DAYS_KEY});

        if (!error) {
            const kind = form.slots[0].days_of_week.length === 7 ? 'Congé' : 'Repos';
            showNotification('success', total > 1
                ? `${total} créneaux de repos enregistrés — envoyés aux caisses`
                : `${kind} ${restModal?.id ? 'modifié' : 'ajouté'} — envoyé aux caisses`);
            setRestModal(null);
            return;
        }

        const message = apiErrorMessage(error, "Impossible d'enregistrer");
        showNotification('error', saved > 0 ? `${saved} créneau(x) sur ${total} enregistré(s). ${message}` : message);
        // Keep only what's left to save: the saved slots must not be sent twice on retry
        if (saved > 0) {
            setRestModal({formKey: Date.now(), initial: {...form, slots: form.slots.slice(saved)}});
        }
    };

    const removeRest = async () => {
        if (!restModal?.id) return;
        setRestSaving(true);
        try {
            await deleteRestDay(restModal.id);
            showNotification('success', 'Supprimé — envoyé aux caisses');
            setRestModal(null);
            queryClient.invalidateQueries({queryKey: REST_DAYS_KEY});
        } catch (error) {
            showNotification('error', apiErrorMessage(error, 'Impossible de supprimer'));
        } finally {
            setRestSaving(false);
        }
    };

    // Opens "Nouveau rendez-vous" pre-filled with the same client, pro, services and slot
    const handleDuplicateEvent = (event) => {
        closeEventModals();
        setDuplicateSource({
            ...event,
            id: undefined,
            status: 'confirmed',
            selectedServices: (event.selectedServices || []).map(name => services.find(sv => sv.name === name)?.id || name),
        });
    };

    const closeEventModals = () => {
        setShowEditModal(false);
        setShowDeleteConfirm(false);
    };

    // RDV as listed in the calendar (service names, client phone), from any copy of it
    const listedEvent = (event) => events.find(e => e.id === event?.id) || event;

    const getEventActions = (rawEvent, {fromModal = false} = {}) => {
        const event = listedEvent(rawEvent);
        if (!event) return [];
        const cancelled = event.status === 'cancelled';
        return [
            !fromModal && {label: 'Ouvrir / modifier', icon: Pencil, onClick: () => handleEventClick(event)},
            event.status === 'pending' && {
                label: 'Confirmer le RDV', icon: CheckCircle2,
                onClick: () => changeEventStatus(event, 'confirmed', {}, 'Rendez-vous confirmé'),
            },
            !cancelled && event.status !== 'completed' && {
                label: 'Marquer terminé', icon: CircleCheckBig,
                onClick: () => changeEventStatus(event, 'completed', {}, 'Rendez-vous marqué comme terminé'),
            },
            cancelled && {
                label: 'Rétablir le RDV', icon: RotateCcw,
                onClick: () => changeEventStatus(event, 'confirmed', {}, 'Rendez-vous rétabli'),
            },
            {
                label: cancelled ? "Renvoyer l'annulation" : 'Renvoyer la confirmation', icon: MessageCircle,
                onClick: () => notifyClientWhatsApp(event),
            },
            {label: 'Dupliquer', icon: Copy, onClick: () => handleDuplicateEvent(event)},
            {divider: true},
            !cancelled && {label: 'Annuler le RDV', icon: CalendarX2, danger: true, onClick: () => setCancelTarget(event)},
            !fromModal && {
                label: 'Supprimer', icon: Trash2, danger: true,
                onClick: () => { setSelectedEvent(event); setShowDeleteConfirm(true); },
            },
        ].filter(Boolean);
    };

    const handleDeleteEvent = async (eventId) => {
        try {
            await deleteEventById(eventId);
            setEvents(events.filter(event => event.id !== eventId));
            setShowDeleteConfirm(false);
            showNotification('success', 'Appointment deleted successfully');
        } catch (error) {
            showNotification('error', error.message);
        }
    };

    // Helper functions for calendar display
    const getEventColor = (status) => {
        switch (status) {
            case 'confirmed':
                return 'bg-gradient-to-r from-green-500 to-green-600 shadow-lg';
            case 'pending':
                return 'bg-gradient-to-r from-yellow-500 to-yellow-600 shadow-lg';
            case 'cancelled':
                return 'bg-gradient-to-r from-red-500 to-red-600 shadow-lg';
            default:
                return 'bg-gradient-to-r from-blue-500 to-blue-600 shadow-lg';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'confirmed':
                return <CheckCircle className="w-3 h-3"/>;
            case 'pending':
                return <AlertCircle className="w-3 h-3"/>;
            case 'cancelled':
                return <XCircle className="w-3 h-3"/>;
            default:
                return <Clock className="w-3 h-3"/>;
        }
    };

    const goToPrevious = () => setCurrentDate(shiftDate(currentDate, currentView, -1));

    const goToNext = () => setCurrentDate(shiftDate(currentDate, currentView, 1));

    const changeView = (view) => {
        if (view === currentView) return;
        // The Day view trusts the API range without re-filtering by date, so
        // drop the previous range's events until the new one is loaded.
        setEvents([]);
        setCurrentView(view);
        try { localStorage.setItem(VIEW_STORAGE_KEY, view); } catch { /* storage unavailable */ }
    };

    // Week header / month cell → open that day in the Day view
    const openDay = (date) => {
        setCurrentDate(date);
        setFilterDate(null);
        changeView('day');
    };

    const goToToday = () => {
        setCurrentDate(new Date());
        setFilterDate(null);
    };

    const handleDateSelect = (date) => {
        setCurrentDate(date);
        setFilterDate(null);
        setShowDatePicker(false);
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Toolbar label for the active view: day, week range or month
    const formatPeriodLabel = (date) => {
        const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
        if (currentView === 'month') {
            return cap(date.toLocaleDateString('fr-FR', {month: 'long', year: 'numeric'}));
        }
        if (currentView === 'week') {
            const days = getWeekDays(date);
            const start = days[0], end = days[6];
            const month = (d) => cap(d.toLocaleDateString('fr-FR', {month: 'short'}));
            return (
                <>
                    {start.getDate()}{start.getMonth() !== end.getMonth() ? ` ${month(start)}` : ''} – {end.getDate()} {month(end)}
                    <span className="hidden sm:inline"> {end.getFullYear()}{isCurrentPeriod(date, 'week') ? ' · Cette semaine' : ''}</span>
                </>
            );
        }
        return formatDateShort(date);
    };

    // Short format for the toolbar button (e.g.  "Lun 21 Avr")
    const formatDateShort = (date) => {
        const weekday = date.toLocaleDateString('fr-FR', { weekday: 'short' });
        const day     = date.getDate();
        const month   = date.toLocaleDateString('fr-FR', { month: 'short' });
        const year    = date.getFullYear();
        // Capitalize first letter
        const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
        // Check if today
        const isToday = new Date().toDateString() === date.toDateString();
        // Year and "Aujourd'hui" only from sm: up, so the toolbar fits one row on phones
        return (
            <>
                {cap(weekday)} {day} {cap(month)}
                <span className="hidden sm:inline"> {year}{isToday ? ' · Aujourd\'hui' : ''}</span>
            </>
        );
    };

    const renderTimeSlots = () => {
        const timeSlots = [];
        for (let hour = 8; hour <= 23; hour++) {
            timeSlots.push({hour, minute: 0});
            if (hour < 23) {
                timeSlots.push({hour, minute: 30});
            }
        }
        return timeSlots;
    };

    const getEventsForView = () => {
        // The API already applies date filters server-side (date_from / date_to as Unix
        // timestamps). Re-filtering on the client by calendar date would silently drop
        // every event whose stored date doesn't match the current view (e.g. events
        // imported from the iPad with an incorrect stored date but correct time-of-day).
        // We therefore trust the API and return all fetched events as-is.
        return [...events];
    };

    const getSortedResources = () => {
        let sorted = [...resources];

        if (searchTerm) {
            sorted = sorted.filter(resource =>
                resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                resource.specialty.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        switch (sortOrder) {
            case 'name-asc':
                return sorted.sort((a, b) => a.name.localeCompare(b.name));
            case 'name-desc':
                return sorted.sort((a, b) => b.name.localeCompare(a.name));
            case 'specialty-asc':
                return sorted.sort((a, b) => a.specialty.localeCompare(b.specialty));
            case 'specialty-desc':
                return sorted.sort((a, b) => b.specialty.localeCompare(a.specialty));
            default:
                return sorted;
        }
    };

    const getEventsForResourceAndTime = (resourceId, timeSlot) => {
        const filteredEvents = getEventsForView().filter(event => {
            // Make sure the event has valid Date objects
            if (!event.start || !event.end || typeof event.start.getHours !== 'function') {
                return false;
            }

            if (event.resourceId !== resourceId) return false;

            const eventStartHour = event.start.getHours();
            const eventStartMinute = event.start.getMinutes();

            // Match events starting at this time slot exactly (:00 or :30)
            // Also match events that started in the previous slot range but span this slot
            const slotTimeMinutes = timeSlot.hour * 60 + timeSlot.minute;
            const eventStartMinutes = eventStartHour * 60 + eventStartMinute;

            // Show event at its start slot (round to nearest 30-min slot)
            const roundedEventStart = Math.floor(eventStartMinutes / 30) * 30;
            const roundedSlot = timeSlot.hour * 60 + timeSlot.minute;

            return roundedEventStart === roundedSlot;
        });

        return filteredEvents;
    };

    const getEventDurationInSlots = (event) => {
        const startTime = event.start.getHours() * 60 + event.start.getMinutes();
        const endTime = event.end.getHours() * 60 + event.end.getMinutes();
        const durationMinutes = endTime - startTime;
        return Math.ceil(durationMinutes / 30);
    };

    const isTimeSlotOccupied = (resourceId, timeSlot) => {
        return getEventsForView().some(event => {
            if (event.resourceId !== resourceId) return false;
            if (!event.start || !event.end || typeof event.start.getHours !== 'function') return false;
            const eventStartTime = event.start.getHours() * 60 + event.start.getMinutes();
            const eventEndTime = event.end.getHours() * 60 + event.end.getMinutes();
            const slotTime = timeSlot.hour * 60 + timeSlot.minute;
            // Occupied if slot is between event start (exclusive) and end (exclusive)
            return slotTime > eventStartTime && slotTime < eventEndTime;
        });
    };

    const handleTimeSlotClick = (resourceId, timeSlot, date) => {
        const start = new Date(date || filterDate || currentDate);
        start.setHours(timeSlot.hour, timeSlot.minute, 0, 0);
        const end = new Date(start);
        end.setMinutes(start.getMinutes() + 30);

        setSelectedTimeSlot({start, end});
        setSelectedResource(resourceId);
        setShowAddModal(true);
    };

    const handleEventClick = async (event) => {
        try {
            setIsLoading(true);


            // Get detailed event information from the API
            const eventDetails = await getEventDetails(event.id);

            // Parse the comma-separated service string into an array
            const serviceList = eventDetails.service ? eventDetails.service.split(',').map(s => s.trim()) : [];

            // Find matching service IDs with a more flexible approach
            const selectedServiceIds = [];

            serviceList.forEach(serviceName => {
                const trimmedName = serviceName.trim().toLowerCase();

                // Try exact match first
                let matchedService = services.find(s =>
                    s.name.toLowerCase() === trimmedName
                );

                // If no exact match, try partial match
                if (!matchedService) {
                    matchedService = services.find(s =>
                        s.name.toLowerCase().includes(trimmedName) ||
                        trimmedName.includes(s.name.toLowerCase())
                    );
                }

                if (matchedService) {
                    selectedServiceIds.push(matchedService.id);
                }
            });

            console.log('Matched service IDs:', selectedServiceIds);

            // Set the selected event with properly formatted services
            setSelectedEvent({
                id: eventDetails.id,
                title: eventDetails.title,
                start: new Date(eventDetails.start),
                end: new Date(eventDetails.end),
                resourceId: eventDetails.employee?.id || event.resourceId,
                clientId: eventDetails.client?.id || event.clientId || null,
                clientName: eventDetails.client
                    ? `${eventDetails.client.firstName || ''} ${eventDetails.client.lastName || ''}`.trim()
                    : (event.clientName || event.title || ''),
                status: eventDetails.status,
                color: eventDetails.color,
                notes: eventDetails.notes || "",
                selectedServices: selectedServiceIds,
                duration: eventDetails.duration,
                externalId: eventDetails.externalId
            });

            setShowEditModal(true);
            setIsLoading(false);
        } catch (error) {
            console.error('Failed to get event details:', error);
            showNotification('error', 'Failed to load event details');
            setIsLoading(false);
        }
    };

    const handleAddToWaitingList = (waitingItem) => {
        const newItem = {
            ...waitingItem,
            id: `wl-${Date.now()}`,
            createdAt: new Date()
        };
        setWaitingList([...waitingList, newItem]);
        setShowWaitingListModal(false);
        showNotification('success', 'Client ajouté à la file d\'attente');
    };

    const handleConvertToAppointment = async (waitingItem, timeSlot) => {
        try {
            const newEvent = await createEvent({
                title: 'Rendez-vous (file d\'attente)',
                start: timeSlot.start.toISOString(),
                end: timeSlot.end.toISOString(),
                resourceId: waitingItem.employeeId,
                clientId: waitingItem.clientId,
                status: 'confirmed',
                notes: waitingItem.notes,
                selectedServices: waitingItem.selectedServices
            });

            setEvents([...events, {
                ...newEvent,
                start: new Date(newEvent.start),
                end: new Date(newEvent.end)
            }]);
            setWaitingList(waitingList.filter(item => item.id !== waitingItem.id));
            setSelectedWaitingItem(null);
            showNotification('success', 'Rendez-vous créé à partir de la file d\'attente');
        } catch (error) {
            showNotification('error', error.message);
        }
    };

    const handleDeleteWaitingItem = (id) => {
        setWaitingList(waitingList.filter(item => item.id !== id));
        showNotification('success', 'Élément supprimé de la file d\'attente');
    };

    // Component definitions
    // ─── Derived lists (depend on events/resources state) ─────────────────────
    const eventCounts = useMemo(() => {
        const counts = {};
        events.forEach(e => { if (e.resourceId != null) counts[e.resourceId] = (counts[e.resourceId] || 0) + 1; });
        return counts;
    }, [events]);

    const visibleResources = useMemo(() => {
        let sorted = [...resources];
        if (searchTerm) sorted = sorted.filter(r =>
            r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (r.specialty || '').toLowerCase().includes(searchTerm.toLowerCase())
        );
        switch (sortOrder) {
            case 'name-asc':       sorted.sort((a,b) => a.name.localeCompare(b.name)); break;
            case 'name-desc':      sorted.sort((a,b) => b.name.localeCompare(a.name)); break;
            case 'specialty-asc':  sorted.sort((a,b) => (a.specialty||'').localeCompare(b.specialty||'')); break;
            case 'specialty-desc': sorted.sort((a,b) => (b.specialty||'').localeCompare(a.specialty||'')); break;
            default: break;
        }
        if (selectedResourceIds === null) return sorted;
        return sorted.filter(r => selectedResourceIds.has(r.id));
    }, [resources, selectedResourceIds, searchTerm, sortOrder]);

    const getEventsForResourceAndDay = useCallback((resourceId) => {
        // The API already applies date_from / date_to filters server-side, so we trust
        // the returned data without re-filtering by date. Just slice by resource.
        return events.filter(e =>
            e.resourceId === resourceId &&
            e.start instanceof Date
        );
    }, [events]);

    if (isLoading) {
        return (
            <div className="h-screen bg-white dark:bg-gray-900">
                <ListLoader label="Chargement du calendrier..."/>
            </div>
        );
    }

    const navButtonClass = 'p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors';

    return (
        <div className="flex flex-col bg-white dark:bg-gray-900" style={{height: '100vh', overflow: 'hidden'}}>
            <Notification
                show={notification.show}
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification({show: false, type: '', message: ''})}
            />

            {/* ── Header + Jour / Semaine / Mois tabs — same layout as the catalogue / stock pages ── */}
            <div className="flex-shrink-0">
                <PageHeader
                    title="Calendrier"
                    tabs={VIEW_TABS}
                    activeTab={currentView}
                    onTabChange={changeView}
                    actions={<>
                        {/* Sync status */}
                        {isSyncing && (
                            <span className="hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary whitespace-nowrap">
                                <RefreshCw className="w-3 h-3 animate-spin"/>Synchronisation…
                            </span>
                        )}
                        {!isSyncing && pendingCount > 0 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 whitespace-nowrap">
                                {pendingCount} en attente
                            </span>
                        )}
                        {failedCount > 0 && (
                            <button onClick={retryFailed}
                                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50 whitespace-nowrap">
                                <AlertTriangle className="w-3 h-3"/>
                                {failedCount} échoué{failedCount > 1 ? 's' : ''} – Réessayer
                            </button>
                        )}
                        {canManageRest && (
                            <HeaderAction icon={CalendarOff} label="Repos / congé" title="Ajouter un repos ou un congé"
                                          onClick={() => openNewRest()}/>
                        )}
                        <HeaderAction icon={Trash2} title="RDV supprimés" onClick={() => setShowDeletedEvents(true)}/>
                        <Button variant="plain" icon={List} title="Voir la file d'attente" className="relative"
                                onClick={() => setShowWaitingListView(true)}>
                            <span className="hidden sm:inline">File d'attente</span>
                            {waitingList.length > 0 && (
                                <span className="ml-1.5 bg-primary text-white rounded-full min-w-[1.25rem] h-5 px-1 inline-flex items-center justify-center text-xs font-bold">
                                    {waitingList.length}
                                </span>
                            )}
                        </Button>
                        <HeaderAction icon={Plus} label="Nouveau" title="Nouveau rendez-vous"
                                      onClick={() => { setSelectedTimeSlot(null); setSelectedResource(null); setShowAddModal(true); }}/>
                    </>}
                />
            </div>

            {/* ── Toolbar: search, sort, day navigation ── */}
            <div className="flex flex-col lg:flex-row flex-shrink-0">
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Rechercher un employé..."/>
                <div className="flex min-w-0">
                    <ToolbarSelect value={sortOrder} onChange={setSortOrder} label="Trier les employés" className="flex-1 lg:flex-none lg:w-48">
                        <option value="name-asc">A → Z</option>
                        <option value="name-desc">Z → A</option>
                        <option value="specialty-asc">Spécialité ↑</option>
                        <option value="specialty-desc">Spécialité ↓</option>
                    </ToolbarSelect>

                    {/* Period navigation (day / week / month) */}
                    <div className="relative flex flex-shrink-0 items-stretch bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-0 shadow-sm">
                        <button onClick={goToPrevious} aria-label={`${PERIOD_LABELS[currentView]} précédent${currentView === 'week' ? 'e' : ''}`} className={navButtonClass}>
                            <ChevronLeft className="w-5 h-5"/>
                        </button>
                        <button
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            className="flex items-center gap-1.5 px-2 sm:px-3 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 whitespace-nowrap capitalize"
                        >
                            <CalendarIcon className="w-4 h-4 text-primary"/>
                            {currentView === 'day' && filterDate ? formatDateShort(filterDate) : formatPeriodLabel(currentDate)}
                            <ChevronDown className="hidden sm:block w-4 h-4 text-gray-400"/>
                        </button>
                        <button onClick={goToNext} aria-label={`${PERIOD_LABELS[currentView]} suivant${currentView === 'week' ? 'e' : ''}`} className={navButtonClass}>
                            <ChevronRight className="w-5 h-5"/>
                        </button>
                        <button
                            onClick={() => { const today = new Date(); goToToday(); loadEventsForCurrentView(today, currentView); }}
                            className="flex items-center gap-1.5 px-3 text-sm font-medium text-primary border-l border-gray-200 dark:border-gray-700 hover:bg-primary/10 whitespace-nowrap"
                            title="Revenir à aujourd'hui et actualiser">
                            <RefreshCw className="w-4 h-4"/>
                            <span className="hidden sm:inline">Aujourd'hui</span>
                        </button>

                        {showDatePicker && (
                            <div className="absolute top-full right-0 mt-1 z-[9999] bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-4">
                                <DateCalendar
                                    value={format(currentDate, 'yyyy-MM-dd')}
                                    onSelect={(iso, {fromGrid}) => {
                                        if (fromGrid) handleDateSelect(parseISO(iso));
                                        else { setCurrentDate(parseISO(iso)); setFilterDate(null); }
                                    }}
                                    onEnter={() => setShowDatePicker(false)}
                                />
                                <div className="flex justify-center mt-2">
                                    <button onClick={() => { goToToday(); setShowDatePicker(false); }}
                                            className="px-3 py-1 text-sm font-medium text-primary rounded hover:bg-primary/10">
                                        Aujourd'hui
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Filtres actifs (teal, Common/ActiveFilters) : recherche + pros sélectionnés ── */}
            <div className="flex-shrink-0">
                <ActiveFilters
                    onClearAll={() => { setSearchTerm(''); selectAllResources(); }}
                    chips={[
                        searchTerm.trim() && {key: 'search', label: `Recherche: ${searchTerm.trim()}`, onRemove: () => setSearchTerm('')},
                        ...(selectedResourceIds === null ? [] : resources
                            .filter(r => selectedResourceIds.has(r.id))
                            .map(r => ({key: `pro-${r.id}`, label: r.name, style: {backgroundColor: r.color}, onRemove: () => toggleResource(r.id)}))),
                    ].filter(Boolean)}
                />
            </div>

            {/* ── Professionals filter + status legend ── */}
            <div className="flex items-center gap-2 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 overflow-x-auto no-scrollbar flex-shrink-0">
                {/* "Tous" chip */}
                <button onClick={selectAllResources}
                    className={`flex-shrink-0 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        selectedResourceIds === null
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}>
                    <span className="w-7 h-7 rounded-lg bg-primary text-white flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4"/>
                    </span>
                    <span className="flex flex-col items-start leading-tight">
                        <span>Tous</span>
                        <span className="text-xs opacity-75">{resources.length} pro{resources.length !== 1 ? 's' : ''}</span>
                    </span>
                </button>

                {/* One chip per professional */}
                {getSortedResources().map(r => {
                    const isOnlyThis = selectedResourceIds !== null && selectedResourceIds.size === 1 && selectedResourceIds.has(r.id);
                    const isFiltered = selectedResourceIds !== null && selectedResourceIds.has(r.id);
                    const isDimmed = selectedResourceIds !== null && !selectedResourceIds.has(r.id);
                    const count = eventCounts[r.id] || 0;
                    return (
                        <button key={r.id}
                            onClick={() => { if (isOnlyThis) selectAllResources(); else selectOnlyResource(r.id); }}
                            aria-pressed={isFiltered}
                            className={`flex-shrink-0 flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                isDimmed ? 'opacity-50' : ''
                            } ${
                                isFiltered
                                    ? 'text-gray-900 dark:text-white'
                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                            style={isFiltered ? {borderColor: r.color, backgroundColor: `${r.color}1A`} : undefined}>
                            <span className="w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs flex-shrink-0 text-white"
                                style={{backgroundColor: r.color}}>
                                {(r.name || '?').charAt(0).toUpperCase()}
                            </span>
                            <span className="flex flex-col items-start leading-tight">
                                <span className="whitespace-nowrap">{r.name}</span>
                                <span className="text-xs opacity-70 whitespace-nowrap">
                                    {count === 0 ? 'Aucun RDV' : `${count} RDV${count > 1 ? 's' : ''}`}
                                </span>
                            </span>
                        </button>
                    );
                })}

                {/* Status legend */}
                <div className="hidden xl:flex items-center gap-3 ml-auto pl-4 flex-shrink-0">
                    {STATUS_OPTIONS.map(({value, label}) => (
                        <span key={value} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${statusStyle(value).dot}`}/>
                            {label}
                        </span>
                    ))}
                </div>
            </div>

            {/* ═══════════════ DAY VIEW — professionals × hours timeline ═══════════════ */}
            {currentView === 'day' && (<>
                {/* ═══════════════ FIXED TIME HEADER ROW ═══════════════ */}
                <div className="flex bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 flex items-center justify-center"
                        style={{width: 144}}>
                        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Professionnels</span>
                    </div>
                    {/* Synced horizontal header */}
                    <div className="flex-1 overflow-x-hidden scrollbar-hide no-scrollbar" style={{scrollbarWidth:'none', msOverflowStyle:'none'}} ref={headerScrollRef} onScroll={syncHScroll}>
                        <div className="flex" style={{width: TOTAL_TIMELINE_WIDTH}}>
                            {TIMELINE_HOURS.map(hour => (
                                <div key={hour}
                                    className="flex-shrink-0 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 py-3 tabular-nums"
                                    style={{width: TIME_SLOT_WIDTH}}>
                                    {hour}:00
                                </div>
                            ))}
                        </div>
                    </div>
                </div>


                {isDayClosed(openingHours, filterDate || currentDate) && (
                    <div className="flex items-center gap-2 px-4 sm:px-6 py-2 text-sm font-medium text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 flex-shrink-0">
                        <CalendarOff className="w-4 h-4"/>
                        Établissement fermé ce jour (horaires d'ouverture)
                    </div>
                )}

                {/* ═══════════════ MAIN SCROLLABLE AREA ═══════════════ */}
                <div className="flex-1 overflow-y-auto scrollbar-hide no-scrollbar bg-white dark:bg-gray-900" style={{scrollbarWidth:'none', msOverflowStyle:'none'}}>
                    {visibleResources.length === 0 ? (
                        <ListEmpty icon={User} title="Aucun professionnel trouvé" text="Modifiez les filtres ou la recherche."/>
                    ) : (
                        visibleResources.map((resource, idx) => {
                            const dayEvents = getEventsForResourceAndDay(resource.id);
                            const laneMeta = computeEventLanes(dayEvents);
                            const maxLanes = dayEvents.length === 0 ? 1 :
                                Math.max(...Object.values(laneMeta).map(m => m.totalLanes));
                            const rowH = Math.max(ROW_HEIGHT, maxLanes * 56 + 16);
                            const day = filterDate || currentDate;
                            const rests = restDaysForDay(restDays, day, resource.id);
                            const offAllDay = rests.find(isFullDayRest);
                            const open = getDayHours(openingHours, day);
                            const hourX = (h) => (Math.min(Math.max(h, TIMELINE_START), TIMELINE_END) - TIMELINE_START) * TIME_SLOT_WIDTH;
                            return (
                                <div key={resource.id} className="flex border-b border-gray-200 dark:border-gray-700"
                                    style={{minHeight: rowH}}>

                                    {/* ── Resource cell ── */}
                                    <div
                                        className="flex-shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center gap-1 p-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
                                        style={{width: 144}}
                                        onClick={() => handleTimeSlotClick(resource.id, {hour: 9, minute: 0})}>
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-base shadow-sm"
                                            style={{backgroundColor: resource.color}}>
                                            {(resource.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 text-center w-full truncate px-1">
                                            {resource.name}
                                        </span>
                                        {offAllDay ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                <CalendarOff className="w-3 h-3"/>{isLeave(offAllDay) ? 'En congé' : 'Repos'}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-400 dark:text-gray-500 text-center w-full truncate px-1">
                                                {resource.specialty}
                                            </span>
                                        )}
                                        <div className="flex items-center gap-1">
                                            <button
                                                className="w-6 h-6 rounded flex items-center justify-center text-primary hover:bg-primary/10 transition-colors"
                                                onClick={(e) => { e.stopPropagation(); handleTimeSlotClick(resource.id, {hour: 9, minute: 0}); }}
                                                title="Ajouter un RDV">
                                                <Plus className="w-3 h-3"/>
                                            </button>
                                            {canManageRest && (
                                                <button
                                                    className="w-6 h-6 rounded flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                                                    onClick={(e) => { e.stopPropagation(); openNewRest(resource.id); }}
                                                    title={`Repos / congé de ${resource.name}`}>
                                                    <CalendarOff className="w-3 h-3"/>
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* ── Timeline ── */}
                                    <div className="flex-1 overflow-x-auto scrollbar-hide no-scrollbar"
                                        style={{scrollbarWidth:'none', msOverflowStyle:'none'}}
                                        ref={el => { rowScrollRefs.current[idx] = el; }}
                                        onScroll={syncHScroll}>
                                        <div className="relative" style={{width: TOTAL_TIMELINE_WIDTH, height: rowH}}>

                                            {/* Hour columns */}
                                            {TIMELINE_HOURS.map((hour, hi) => (
                                                <div key={hour}
                                                    className="absolute top-0 bottom-0 border-r border-gray-100 dark:border-gray-700 hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer transition-colors"
                                                    style={{left: hi * TIME_SLOT_WIDTH, width: TIME_SLOT_WIDTH}}
                                                    onClick={() => handleTimeSlotClick(resource.id, {hour, minute: 0})}
                                                />
                                            ))}

                                            {/* Before / after opening hours */}
                                            {open && !open.closed && [[TIMELINE_START, open.start], [open.end, TIMELINE_END]]
                                                .filter(([a, b]) => hourX(b) > hourX(a))
                                                .map(([a, b]) => (
                                                    <div key={`closed-${a}`} className="absolute top-0 bottom-0 z-[1] pointer-events-none bg-gray-100/50 dark:bg-gray-800/50"
                                                         style={{...REST_HATCH, left: hourX(a), width: hourX(b) - hourX(a)}}/>
                                                ))}

                                            {/* Repos / congé — not bookable, click to edit */}
                                            {rests.map(rest => {
                                                const left = hourX(Number(rest.start_time));
                                                const width = hourX(Number(rest.end_time)) - left;
                                                if (width <= 0) return null;
                                                return (
                                                    <button key={rest.id} type="button" onClick={() => openEditRest(rest)}
                                                            title={`${restKindLabel(rest)} · ${restHoursLabel(rest)}${canManageRest ? ' — cliquer pour modifier' : ''}`}
                                                            className={`absolute top-0 bottom-0 z-[2] flex flex-col items-start justify-center px-3 text-left bg-gray-100/80 dark:bg-gray-800/80 border-x border-dashed border-gray-300 dark:border-gray-600 transition-colors ${
                                                                canManageRest ? 'hover:bg-gray-200/70 dark:hover:bg-gray-700/70' : 'cursor-default'
                                                            }`}
                                                            style={{...REST_HATCH, left, width}}>
                                                        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300">
                                                            <CalendarOff className="w-3.5 h-3.5"/>{restKindLabel(rest)}
                                                        </span>
                                                        <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                                                            {restHoursLabel(rest)}{isLeave(rest) ? ` · jusqu'au ${rest.end_date.split('-').reverse().join('/')}` : ''}
                                                        </span>
                                                    </button>
                                                );
                                            })}

                                            {/* Event cards */}
                                            {dayEvents.map(event => {
                                                const left  = getEventLeft(event);
                                                const width = getEventWidth(event);
                                                const { lane, totalLanes } = laneMeta[event.id] || { lane: 0, totalLanes: 1 };
                                                const laneH = (rowH - 16) / totalLanes;
                                                const top   = 8 + lane * laneH;
                                                const height = Math.max(laneH - 4, 24);
                                                return (
                                                    <AppointmentCard key={event.id}
                                                                     event={event}
                                                                     resource={resource}
                                                                     height={height}
                                                                     onClick={handleEventClick}
                                                                     getActions={getEventActions}
                                                                     style={{left, width, top}}/>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}

                    {/* Empty state when no events for the day */}
                    {events.length === 0 && visibleResources.length > 0 && (
                        <ListEmpty
                            icon={CalendarIcon}
                            title="Aucun rendez-vous prévu"
                            text={new Date().toDateString() === currentDate.toDateString()
                                ? "Aucun rendez-vous n'est prévu pour aujourd'hui."
                                : "Aucun rendez-vous n'est prévu pour cette journée."}
                            action={(
                                <Button variant="primary" icon={Plus} className="mt-4" onClick={() => setShowAddModal(true)}>
                                    Créer un rendez-vous
                                </Button>
                            )}
                        />
                    )}
                </div>
            </>)}

            {/* ═══════════════ WEEK VIEW — days × hours agenda ═══════════════ */}
            {currentView === 'week' && (
                <WeekView
                    currentDate={currentDate}
                    events={events}
                    resources={visibleResources}
                    hours={TIMELINE_HOURS}
                    onSlotClick={(date, slot) => handleTimeSlotClick(visibleResources.length === 1 ? visibleResources[0].id : null, slot, date)}
                    onEventClick={handleEventClick}
                    getEventActions={getEventActions}
                    onDayClick={openDay}
                    restDays={restDays}
                    openingHours={openingHours}
                    onRestClick={openEditRest}
                />
            )}

            {/* ═══════════════ MONTH VIEW — calendar grid ═══════════════ */}
            {currentView === 'month' && (
                <MonthView
                    currentDate={currentDate}
                    events={events}
                    resources={visibleResources}
                    onDayClick={openDay}
                    onEventClick={handleEventClick}
                    restDays={restDays}
                    openingHours={openingHours}
                />
            )}

            {/* ═══════════════ MODALS ═══════════════ */}
            {showAddModal && (
                <EventModal
                    onClose={() => setShowAddModal(false)}
                    onSave={handleAddEvent}
                    selectedTimeSlot={selectedTimeSlot}
                    selectedResource={selectedResource}
                    resources={resources}
                    clients={clients}
                    services={services}
                    restDays={restDays}
                    isEdit={false}
                />
            )}

            {showEditModal && selectedEvent && (
                <EventModal
                    event={selectedEvent}
                    onClose={() => setShowEditModal(false)}
                    onSave={handleUpdateEvent}
                    onDelete={() => setShowDeleteConfirm(true)}
                    actions={getEventActions(selectedEvent, {fromModal: true})}
                    resources={resources}
                    clients={clients}
                    services={services}
                    restDays={restDays}
                    isEdit={true}
                />
            )}

            {duplicateSource && (
                <EventModal
                    event={duplicateSource}
                    title="Dupliquer le rendez-vous"
                    onClose={() => setDuplicateSource(null)}
                    onSave={(eventData) => { setDuplicateSource(null); handleAddEvent(eventData); }}
                    resources={resources}
                    clients={clients}
                    services={services}
                    restDays={restDays}
                    isEdit={false}
                />
            )}

            {restModal && (
                <RestDayModal
                    key={restModal.formKey}
                    initial={restModal.initial}
                    editing={Boolean(restModal.id)}
                    resources={resources}
                    saving={restSaving}
                    onSave={saveRest}
                    onDelete={removeRest}
                    onClose={() => setRestModal(null)}
                />
            )}

            {cancelTarget && (
                <CancelEventModal
                    event={cancelTarget}
                    onConfirm={(options) => handleCancelAppointment(cancelTarget, options)}
                    onCancel={() => setCancelTarget(null)}
                />
            )}

            {showDeleteConfirm && selectedEvent && (
                <DeleteConfirmModal
                    event={selectedEvent}
                    onConfirm={() => handleDeleteEvent(selectedEvent.id)}
                    onCancel={() => setShowDeleteConfirm(false)}
                />
            )}

            {showWaitingListModal && (
                <WaitingListModal
                    onClose={() => setShowWaitingListModal(false)}
                    onSave={handleAddToWaitingList}
                    services={services}
                    resources={resources}
                    clients={clients}
                />
            )}

            {showWaitingListView && (
                <WaitingListView
                    waitingList={waitingList}
                    onClose={() => setShowWaitingListView(false)}
                    onSchedule={(item) => {
                        setSelectedWaitingItem(item);
                        setShowWaitingListView(false);
                    }}
                    onAdd={() => {
                        setShowWaitingListView(false);
                        setShowWaitingListModal(true);
                    }}
                    onDelete={handleDeleteWaitingItem}
                    services={services}
                />
            )}

            {selectedWaitingItem && (
                <EventModal
                    onClose={() => setSelectedWaitingItem(null)}
                    onSave={(eventData) => {
                        handleConvertToAppointment(selectedWaitingItem, {start: eventData.start, end: eventData.end});
                        handleAddEvent(eventData);
                    }}
                    selectedResource={selectedWaitingItem.employeeId}
                    resources={resources}
                    clients={clients}
                    services={services}
                    restDays={restDays}
                    isEdit={false}
                    initialClientId={selectedWaitingItem.clientId}
                />
            )}

            {showDeletedEvents && (
                <DeletedEventsModal
                    onClose={() => setShowDeletedEvents(false)}
                    onRestored={() => {
                        showNotification('success', 'Rendez-vous restauré');
                        loadEventsForCurrentView();
                    }}
                />
            )}
        </div>
    );
};

export default Calendar;
